@if (isLoading()) {
  <div class="flex flex-col items-center justify-center min-h-[50vh] p-8 space-y-4">
    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-9"></div>
    <p class="text-sm font-medium text-neutral-10 animate-pulse">Chargement de la fiche de l'agent...</p>
  </div>
} @else if (hasError() || !agentProfile()) {
  <div
    class="max-w-2xl mx-auto my-12 p-8 bg-white dark:bg-neutral-2 rounded-3xl border border-neutral-3 dark:border-neutral-4 shadow-sm text-center space-y-5"
  >
    <div
      class="w-16 h-16 rounded-2xl bg-neutral-2 dark:bg-neutral-3 text-neutral-10 dark:text-neutral-11 mx-auto flex items-center justify-center shadow-xs"
    >
      <mat-icon class="w-8! h-8! text-3xl!">person_off</mat-icon>
    </div>
    <div class="space-y-2">
      <h2 class="text-xl font-bold text-neutral-12">Profil d'agent immobilier indisponible</h2>
      <p class="text-sm text-neutral-10 max-w-md mx-auto leading-relaxed">
        Ce profil n'existe pas ou a été configuré en mode privé par son titulaire. Vous pouvez explorer les
        autres agents certifiés de votre secteur.
      </p>
    </div>
    <div class="pt-2">
      <a
        routerLink="/"
        class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm bg-primary-9 hover:bg-primary-10 text-white transition-all shadow-sm"
      >
        <mat-icon class="text-white! w-4! h-4! text-base!">arrow_back</mat-icon>
        <span>Retour à l'accueil</span>
      </a>
    </div>
  </div>
} @else if (agentProfile(); as profile) {
  <div class="w-full max-w-5xl mx-auto py-8 space-y-8">
    <!-- Vacation Mode Notice -->
    @if (isVacationMode()) {
      <div
        class="flex items-center gap-3 p-4 bg-amber-1 dark:bg-amber-2/30 border border-amber-4 dark:border-amber-7 rounded-2xl text-amber-12 text-sm shadow-xs"
      >
        <mat-icon class="text-amber-11! shrink-0">beach_access</mat-icon>
        <span
          >Cet agent immobilier est actuellement en congés ou indisponible pour de nouveaux mandats
          immédiats.</span
        >
      </div>
    }

    <!-- ==========================================
         HERO SECTION: AGENT IDENTITY & CREDENTIALS
         ========================================== -->
    <header
      class="bg-white dark:bg-neutral-2 rounded-3xl border border-neutral-3 dark:border-neutral-4 p-6 sm:p-8 shadow-xs relative overflow-hidden"
    >
      <!-- Background subtle gradient accent -->
      <div
        class="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-primary-2/40 dark:bg-primary-9/10 blur-3xl pointer-events-none"
      ></div>

      <div class="flex flex-col sm:flex-row items-start sm:items-center gap-6 relative z-10">
        <!-- Agent Avatar -->
        <div class="relative shrink-0">
          @if (effectiveAvatarUrl()) {
            <img
              [src]="effectiveAvatarUrl()!"
              [alt]="fullName()"
              class="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl object-cover border-2 border-white dark:border-neutral-3 shadow-md"
            />
          } @else {
            <div
              class="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-linear-to-br from-primary-3 to-primary-6 dark:from-primary-8 dark:to-primary-9 text-primary-12 dark:text-white flex items-center justify-center font-extrabold text-2xl sm:text-3xl shadow-md border-2 border-white dark:border-neutral-3"
            >
              {{ initials() }}
            </div>
          }

          <!-- Certified check badge -->
          @if (isCardActive()) {
            <div
              class="absolute -bottom-1.5 -right-1.5 w-8 h-8 rounded-full bg-jade-9 text-white flex items-center justify-center shadow-md border-2 border-white dark:border-neutral-2"
              matTooltip="Habilité à exercer (carte professionnelle en cours de validité)"
            >
              <mat-icon
                class="text-white! w-5! h-5! text-[20px]! leading-none! flex items-center justify-center"
                >verified_user</mat-icon
              >
            </div>
          }
        </div>

        <!-- Identity & Badges -->
        <div class="flex-1 min-w-0 space-y-2">
          <div class="flex items-center gap-3 flex-wrap">
            <h1 class="text-2xl sm:text-3xl font-extrabold text-neutral-12 tracking-tight">
              {{ fullName() }}
            </h1>

            @if (network()) {
              <span
                class="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-primary-2 dark:bg-primary-3/30 text-primary-11 border border-primary-4 dark:border-primary-5/40 shadow-2xs"
              >
                {{ network() }}
              </span>
            }
          </div>

          <div class="flex items-center gap-2 flex-wrap text-sm text-neutral-11 font-medium">
            <span class="inline-flex items-center gap-1">
              <mat-icon class="w-4! h-4! text-base! text-primary-9">badge</mat-icon>
              <span>{{
                profile.type === 'titulaire' ? 'Titulaire de carte T' : 'Mandataire immobilier'
              }}</span>
            </span>

            @if (activeCard()?.siren) {
              <span class="text-neutral-6">•</span>
              <span class="font-mono text-xs text-neutral-9 dark:text-neutral-10"
                >SIREN {{ activeCard()!.siren }}</span
              >
            }

            @if (primaryRegion()) {
              <span class="text-neutral-6">•</span>
              <span class="inline-flex items-center gap-1 text-neutral-11">
                <mat-icon class="w-4! h-4! text-base! text-neutral-9">location_on</mat-icon>
                <span>{{ getRegionName(primaryRegion()) }}</span>
              </span>
            }
          </div>

          <!-- Trust points banner -->
          <div class="pt-2 flex items-center gap-4 flex-wrap text-xs font-semibold">
            @if (averageCommissionRate()) {
              <div
                class="flex items-center gap-1.5 text-primary-11 bg-primary-1 dark:bg-primary-3/20 px-3 py-1 rounded-lg border border-primary-3 dark:border-primary-5/40"
              >
                <mat-icon class="text-primary-9! w-4! h-4! text-base!">percent</mat-icon>
                <span>Honoraires indicatifs dès {{ averageCommissionRate() | number: '1.1-2' }}%</span>
              </div>
            }
          </div>
        </div>
      </div>
    </header>

    <!-- =========================================================================
         THE CORNERSTONE CONVERSION BOX: "METTRE CET AGENT EN CONCURRENCE"
         Anti-leakage / Anti-court-circuit value proposition
         ========================================================================= -->
    @if (showPublicCtas()) {
      <section
        class="@container bg-linear-to-br from-primary-1 via-white to-primary-2 dark:from-neutral-2 dark:via-neutral-2 dark:to-primary-950/30 rounded-3xl border-2 border-primary-4 dark:border-primary-8/40 p-6 sm:p-8 shadow-sm relative overflow-hidden space-y-6"
      >
        <div
          class="flex flex-col md:flex-row @lg:flex-row items-stretch md:items-center @lg:items-center justify-between gap-6"
        >
          <div class="space-y-3 flex-1 min-w-0 max-w-2xl">
            <div
              class="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider bg-primary-3 dark:bg-primary-4/30 text-primary-11 border border-primary-5 dark:border-primary-6/40 shadow-2xs"
            >
              <mat-icon class="w-4! h-4! text-base!">auto_awesome</mat-icon>
              <span>La concurrence protège vos intérêts</span>
            </div>

            <h2 class="text-xl sm:text-2xl font-black text-neutral-12 leading-tight">
              Vendez au meilleur prix en invitant cet agent sur votre bien
            </h2>

            <p class="text-sm text-neutral-11 leading-relaxed">
              Ne contactez pas cet agent de manière isolée: sur ETXE, mettez-le en compétition avec jusqu'à 4
              confrères certifiés de votre secteur. Vous obtenez ainsi sa
              <strong>meilleure proposition d'honoraires</strong> et des
              <strong>engagements contractuels fermes</strong>.
            </p>

            <!-- Key Guarantees List -->
            <div class="flex flex-wrap items-center gap-x-6 gap-y-2 pt-2">
              <div class="flex items-center gap-2 text-xs font-semibold text-neutral-12">
                <mat-icon class="text-primary-9! w-4! h-4! text-base! shrink-0">trending_down</mat-icon>
                <span>Honoraires d'agence négociés</span>
              </div>
              <div class="flex items-center gap-2 text-xs font-semibold text-neutral-12">
                <mat-icon class="text-primary-9! w-4! h-4! text-base! shrink-0">task_alt</mat-icon>
                <span>Engagements écrits</span>
              </div>
              <div class="flex items-center gap-2 text-xs font-semibold text-neutral-12">
                <mat-icon class="text-primary-9! w-4! h-4! text-base! shrink-0">lock_open</mat-icon>
                <span>100% gratuit</span>
              </div>
            </div>
          </div>

          <!-- The Main Call-To-Action Button -->
          <div
            class="shrink-0 flex flex-col items-stretch sm:items-center gap-2 text-center w-full md:w-auto"
          >
            <a
              [href]="ownerAppUrl"
              class="inline-flex items-center justify-center gap-3 px-7 py-4 rounded-lg bg-primary-9 hover:bg-primary-10 text-white font-black text-base shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 cursor-pointer text-center w-full sm:w-auto whitespace-nowrap"
            >
              <mat-icon class="text-white! w-5! h-5! text-xl! shrink-0">campaign</mat-icon>
              <span>Inviter cet agent sur mon bien</span>
            </a>
            <span class="text-[11px] text-neutral-10 font-medium text-center"
              >Création de dossier en 5 min</span
            >
          </div>
        </div>
      </section>
    }

    <!-- ==========================================
         BIOGRAPHY / PRESENTATION
         ========================================== -->
    @if (bio()) {
      <section
        class="bg-white dark:bg-neutral-2 rounded-3xl border border-neutral-3 dark:border-neutral-4 p-6 sm:p-7 shadow-xs space-y-3"
      >
        <h3 class="text-base font-bold text-neutral-12 flex items-center gap-2">
          <mat-icon class="text-primary-9! size-5">description</mat-icon>
          <span>Présentation et philosophie</span>
        </h3>
        <p class="text-sm text-neutral-11 leading-relaxed whitespace-pre-line">
          {{ bio() }}
        </p>
      </section>
    }

    <!-- ==========================================
         INTERVENTION SECTOR & CITIES
         ========================================== -->
    <section
      class="bg-white dark:bg-neutral-2 rounded-3xl border border-neutral-3 dark:border-neutral-4 p-6 sm:p-7 shadow-xs space-y-5"
    >
      <h3 class="text-base font-bold text-neutral-12 flex items-center gap-2">
        <mat-icon class="text-primary-9! size-5">explore</mat-icon>
        <span>Secteur d'intervention géographique</span>
      </h3>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <!-- Région & Départements -->
        <div
          class="p-4 rounded-2xl bg-neutral-1 dark:bg-neutral-3/40 border border-neutral-3 dark:border-neutral-4 space-y-2"
        >
          <span class="text-xs font-bold text-neutral-9 dark:text-neutral-11 uppercase tracking-wider"
            >Région principale</span
          >
          <div class="text-sm font-extrabold text-neutral-12">
            {{ getRegionName(primaryRegion()) || 'France entière' }}
          </div>

          @if (interventionZones().length > 0) {
            @let depts = interventionZones()[0].departments;
            @if (depts && depts.length > 0) {
              <div class="pt-2 flex flex-wrap gap-1.5">
                @for (d of depts; track d) {
                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-white dark:bg-neutral-2 text-neutral-11 border border-neutral-3 dark:border-neutral-4"
                  >
                    {{ getDepartmentLabel(d) }}
                  </span>
                }
              </div>
            }
          }
        </div>

        <!-- Communes cibles -->
        <div
          class="p-4 rounded-2xl bg-neutral-1 dark:bg-neutral-3/40 border border-neutral-3 dark:border-neutral-4 space-y-2"
        >
          <span class="text-xs font-bold text-neutral-9 dark:text-neutral-11 uppercase tracking-wider"
            >Communes prioritaires suivies</span
          >
          @if (preferredCities().length > 0) {
            <div class="flex flex-wrap gap-2 pt-1">
              @for (city of preferredCities(); track city.name + '-' + city.zipCode) {
                <span
                  class="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-bold bg-white dark:bg-neutral-2 text-neutral-12 border border-neutral-3 dark:border-neutral-4 shadow-2xs"
                >
                  <mat-icon class="text-primary-9! w-3! h-3! text-xs!">place</mat-icon>
                  <span>{{ city.name }} ({{ city.zipCode }})</span>
                </span>
              }
            </div>
          } @else {
            <p class="text-xs text-neutral-9 dark:text-neutral-11 italic pt-1">
              Intervention sur l'ensemble de la région et départements limitrophes.
            </p>
          }
        </div>
      </div>
    </section>

    <!-- ==========================================
         SPECIALTIES & SERVICES
         ========================================== -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <!-- Spécialités -->
      <section
        class="bg-white dark:bg-neutral-2 rounded-3xl border border-neutral-3 dark:border-neutral-4 p-6 sm:p-7 shadow-xs space-y-4"
      >
        <h3 class="text-base font-bold text-neutral-12 flex items-center gap-2">
          <mat-icon class="text-primary-9! size-5">category</mat-icon>
          <span>Spécialités de transactions</span>
        </h3>

        @if (specialties().length > 0) {
          <div class="flex flex-wrap gap-2.5">
            @for (spec of specialties(); track spec) {
              @let info = getSpecialtyInfo(spec);
              <div
                class="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold bg-neutral-1 dark:bg-neutral-3/50 text-neutral-12 border border-neutral-3 dark:border-neutral-4 shadow-2xs"
              >
                <mat-icon class="text-primary-9! w-4! h-4! text-base!">{{ info.icon }}</mat-icon>
                <span>{{ info.label }}</span>
              </div>
            }
          </div>
        } @else {
          <p class="text-xs text-neutral-9 dark:text-neutral-11 italic">
            Spécialiste de toutes transactions résidentielles.
          </p>
        }
      </section>

      <!-- Services inclus dans le mandat -->
      <section
        class="bg-white dark:bg-neutral-2 rounded-3xl border border-neutral-3 dark:border-neutral-4 p-6 sm:p-7 shadow-xs space-y-4"
      >
        <h3 class="text-base font-bold text-neutral-12 flex items-center gap-2">
          <mat-icon class="text-primary-9! size-5">stars</mat-icon>
          <span>Prestations et services inclus</span>
        </h3>

        @if (services().length > 0) {
          <div class="flex flex-wrap gap-2.5">
            @for (srv of services(); track srv) {
              @let info = getServiceInfo(srv);
              <div
                class="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold bg-neutral-1 dark:bg-neutral-3/50 text-neutral-12 border border-neutral-3 dark:border-neutral-4 shadow-2xs"
              >
                <mat-icon class="text-primary-9! w-4! h-4! text-base!">{{ info.icon }}</mat-icon>
                <span>{{ info.label }}</span>
              </div>
            }
          </div>
        } @else {
          <p class="text-xs text-neutral-9 dark:text-neutral-11 italic">
            Prestations personnalisées selon le type de mandat proposé.
          </p>
        }
      </section>
    </div>

    <!-- ==========================================
         CONTACT INFOS WITH OBFUSCATION UX
         ========================================== -->
    <section
      class="bg-white dark:bg-neutral-2 rounded-3xl border border-neutral-3 dark:border-neutral-4 p-6 sm:p-7 shadow-xs space-y-4"
    >
      <div class="flex items-center justify-between flex-wrap gap-2">
        <h3 class="text-base font-bold text-neutral-12 flex items-center gap-2">
          <mat-icon class="text-primary-9 size-5">contact_phone</mat-icon>
          <span>Coordonnées professionnelles et messagerie</span>
        </h3>

        <span
          class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-neutral-2 dark:bg-neutral-3 text-neutral-11 border border-neutral-3 dark:border-neutral-4"
        >
          <mat-icon class="w-3.5! h-3.5! text-xs! text-neutral-8 dark:text-neutral-10"
            >visibility_off</mat-icon
          >
          <span>Coordonnées sécurisées</span>
        </span>
      </div>

      <!-- Masked Fields Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- Phone Field -->
        <div
          class="p-4 bg-neutral-1/50 dark:bg-neutral-3/30 rounded-2xl border border-neutral-3 dark:border-neutral-4 flex items-center justify-between gap-3"
        >
          <div class="flex items-center gap-3 min-w-0">
            <div
              class="w-10 h-10 rounded-xl bg-neutral-2 dark:bg-neutral-3 text-neutral-10 dark:text-neutral-11 flex items-center justify-center shrink-0 border border-neutral-3 dark:border-neutral-4"
            >
              <mat-icon class="w-5! h-5! text-xl!">call</mat-icon>
            </div>
            <div class="flex flex-col min-w-0">
              <span class="text-xs text-neutral-9 dark:text-neutral-11 font-medium"
                >Ligne téléphonique pro</span
              >
              <span class="text-sm font-bold text-neutral-12 font-mono tracking-wider">
                {{ contactInfos()?.mobileNumber || '06 •• •• •• ••' }}
              </span>
            </div>
          </div>

          <span
            class="text-[10px] font-bold px-2.5 py-1 rounded-full border bg-neutral-2 dark:bg-neutral-3 text-neutral-10 dark:text-neutral-11 border-neutral-4 dark:border-neutral-5 shrink-0"
            matTooltip="Débloqué dès que l'agent candidate sur votre bien"
          >
            Offusqué
          </span>
        </div>

        <!-- Email Field -->
        <div
          class="p-4 bg-neutral-1/50 dark:bg-neutral-3/30 rounded-2xl border border-neutral-3 dark:border-neutral-4 flex items-center justify-between gap-3"
        >
          <div class="flex items-center gap-3 min-w-0">
            <div
              class="w-10 h-10 rounded-xl bg-neutral-2 dark:bg-neutral-3 text-neutral-10 dark:text-neutral-11 flex items-center justify-center shrink-0 border border-neutral-3 dark:border-neutral-4"
            >
              <mat-icon class="w-5! h-5! text-xl!">alternate_email</mat-icon>
            </div>
            <div class="flex flex-col min-w-0">
              <span class="text-xs text-neutral-9 dark:text-neutral-11 font-medium">Email professionnel</span>
              <span class="text-sm font-bold text-neutral-12 font-mono truncate">
                {{ contactInfos()?.email || '•••••@agence.immo' }}
              </span>
            </div>
          </div>

          <span
            class="text-[10px] font-bold px-2.5 py-1 rounded-full border bg-neutral-2 dark:bg-neutral-3 text-neutral-10 dark:text-neutral-11 border-neutral-4 dark:border-neutral-5 shrink-0"
            matTooltip="Débloqué dès que l'agent candidate sur votre bien"
          >
            Offusqué
          </span>
        </div>
      </div>

      <!-- Pedagogical Note -->
      @if (showPublicCtas()) {
        <div
          class="p-3.5 bg-primary-1 dark:bg-primary-3/20 border border-primary-3 dark:border-primary-5/40 rounded-2xl flex items-start gap-3 text-xs text-neutral-11 leading-relaxed"
        >
          <mat-icon class="w-4.5! h-4.5! text-lg! text-primary-9 shrink-0 mt-0.5">info</mat-icon>
          <div>
            <strong class="text-primary-11">Pourquoi ces coordonnées sont-elles offusquées ?</strong>
            En appelant un agent en direct, vous perdez le levier de la mise en concurrence et vous exposez à
            ses honoraires de barème standard. En publiant votre bien sur ETXE, vous bénéficiez de
            <strong>sa proposition négociée sous 48h</strong> et débloquez la messagerie directe pour convenir
            d'un rendez-vous.
          </div>
        </div>
      }
    </section>

    <!-- ==========================================
         OPENING HOURS & AVAILABILITY
         ========================================== -->
    @if (hasOpeningHours()) {
      <section
        class="bg-white dark:bg-neutral-2 rounded-3xl border border-neutral-3 dark:border-neutral-4 p-6 sm:p-7 shadow-xs space-y-4"
      >
        <div class="flex items-center justify-between flex-wrap gap-2">
          <h3 class="text-base font-bold text-neutral-12 flex items-center gap-2">
            <mat-icon class="text-primary-9! size-5">schedule</mat-icon>
            <span>Horaires de disponibilité</span>
          </h3>

          @if (openingHoursStatus(); as status) {
            <span
              class="text-xs font-bold px-2.5 py-1 rounded-full border inline-flex items-center gap-1.5 shadow-2xs"
              [class]="status.badgeClass"
            >
              <span
                class="w-2 h-2 rounded-full"
                [class.bg-primary-9]="status.isOpen"
                [class.bg-neutral-6]="!status.isOpen"
              ></span>
              {{ status.text }}
            </span>
          }
        </div>

        <aura-opening-hours-display [hours]="openingHours() ?? undefined" [showTitle]="false" />
      </section>
    }

    <!-- ==========================================
         OFFICIAL CARTE T 3D GUARANTEE
         ========================================== -->
    @if (activeCard(); as card) {
      <section
        class="@container bg-white dark:bg-neutral-2 rounded-3xl border border-neutral-3 dark:border-neutral-4 p-6 sm:p-8 shadow-xs space-y-6"
      >
        <div class="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 class="text-base font-bold text-neutral-12 flex items-center gap-2">
              <mat-icon class="text-primary-9! size-5">verified_user</mat-icon>
              <span>Garantie légale de la carte professionnelle T</span>
            </h3>
            <p class="text-xs text-neutral-10 mt-0.5">
              Délivrée par la Chambre de Commerce et d'Industrie (Loi Hoguet n° 70-9 du 2 janvier 1970).
            </p>
          </div>

          <span
            class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-jade-3 dark:bg-jade-3/30 text-jade-11 border border-jade-5 dark:border-jade-6/40 shrink-0"
          >
            <mat-icon class="text-jade-9! w-4! h-4! text-sm! shrink-0">check_circle</mat-icon>
            <span>Carte active et vérifiée</span>
          </span>
        </div>

        <div class="flex flex-col md:flex-row @lg:flex-row items-center gap-6 sm:gap-8">
          <!-- 3D Card Display -->
          <div class="w-full max-w-101 shrink-0">
            <etxe-agent-card-display [card]="card"></etxe-agent-card-display>
          </div>

          <!-- Legal Metadata Grid -->
          <div
            class="flex-1 w-full min-w-0 grid grid-cols-1 xl:grid-cols-2 @2xl:grid-cols-2 gap-y-3 gap-x-6 text-xs text-neutral-11"
          >
            <div class="flex flex-col pb-2 border-b border-neutral-2 dark:border-neutral-3 min-w-0">
              <span class="text-neutral-9 dark:text-neutral-10 font-medium">Titulaire de la carte</span>
              <span class="font-bold text-neutral-12 mt-0.5 truncate" [title]="card.companyName">{{
                card.companyName
              }}</span>
            </div>
            <div class="flex flex-col pb-2 border-b border-neutral-2 dark:border-neutral-3 min-w-0">
              <span class="text-neutral-9 dark:text-neutral-10 font-medium">Représentant légal</span>
              <span
                class="font-bold text-neutral-12 mt-0.5 truncate"
                [title]="card.leader.firstName + ' ' + (card.leader.lastName | uppercase)"
                >{{ card.leader.firstName }} {{ card.leader.lastName | uppercase }}</span
              >
            </div>
            <div class="flex flex-col pb-2 border-b border-neutral-2 dark:border-neutral-3 min-w-0">
              <span class="text-neutral-9 dark:text-neutral-10 font-medium">Numéro de carte</span>
              <span class="font-bold text-neutral-12 mt-0.5 font-mono break-all">{{ card.number }}</span>
            </div>
            <div class="flex flex-col pb-2 border-b border-neutral-2 dark:border-neutral-3 min-w-0">
              <span class="text-neutral-9 dark:text-neutral-10 font-medium">CCI émettrice</span>
              <span class="font-bold text-neutral-12 mt-0.5 truncate" [title]="card.cci.name">{{
                card.cci.name
              }}</span>
            </div>
            <div class="flex flex-col pb-2 border-b border-neutral-2 dark:border-neutral-3 min-w-0">
              <span class="text-neutral-9 dark:text-neutral-10 font-medium">Date de fin de validité</span>
              <span class="font-bold text-neutral-12 mt-0.5">{{ card.expiresAt | date: 'dd/MM/yyyy' }}</span>
            </div>
            <div class="flex flex-col pb-2 border-b border-neutral-2 dark:border-neutral-3 min-w-0">
              <span class="text-neutral-9 dark:text-neutral-10 font-medium">Mention légale</span>
              <span class="font-bold text-jade-11 mt-0.5"
                >Transaction sur immeubles et fonds de commerce</span
              >
            </div>
          </div>
        </div>
      </section>
    }

    <!-- ==========================================
         BOTTOM CTA: RE-ENGAGE
         ========================================== -->
    @if (showPublicCtas()) {
      <div
        class="relative overflow-hidden rounded-3xl p-8 text-center space-y-4 shadow-md bg-primary-9 dark:bg-neutral-2 dark:border dark:border-neutral-4 text-white dark:text-neutral-12"
      >
        <!-- Ambient decorative glow in dark mode -->
        <div
          class="hidden dark:block absolute -right-16 -top-16 w-64 h-64 rounded-full bg-primary-9/15 blur-3xl pointer-events-none"
        ></div>
        <div
          class="hidden dark:block absolute -left-16 -bottom-16 w-64 h-64 rounded-full bg-primary-9/10 blur-3xl pointer-events-none"
        ></div>

        <h3 class="text-xl sm:text-2xl font-black text-white dark:text-neutral-12 relative z-10">
          Prêt à comparer les offres pour votre vente immobilière ?
        </h3>
        <p class="text-sm text-white/85 dark:text-neutral-11 max-w-xl mx-auto leading-relaxed relative z-10">
          Publiez votre bien en quelques clics. {{ fullName() }} et d'autres professionnels certifiés
          recevront votre demande et formuleront leurs propositions sous 48h.
        </p>
        <div class="pt-2 relative z-10">
          <a
            [href]="ownerAppUrl"
            class="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-white dark:bg-primary-9 text-primary-11 dark:text-white hover:bg-white/90 dark:hover:bg-primary-10 font-black text-base shadow-lg transition-all transform hover:-translate-y-0.5 cursor-pointer"
          >
            <span>Déposer mon bien gratuitement</span>
            <mat-icon class="text-primary-11! dark:text-white! w-5! h-5! text-xl! shrink-0"
              >arrow_forward</mat-icon
            >
          </a>
        </div>
      </div>
    }
  </div>
}
